import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

export default function Contact(){
  const { t } = useTranslation()
  const [status, setStatus] = useState<'idle'|'sending'|'sent'|'error'>('idle')
  const [error, setError] = useState<string|undefined>()

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(undefined)
    const form = e.currentTarget
    const data = new FormData(form)
    const subject = (data.get('subject') as string || '').trim()
    const message = (data.get('message') as string || '').trim()
    const name = (data.get('name') as string || '').trim()
    const email = (data.get('email') as string || '').trim()
    const phone = (data.get('phone') as string || '').trim()
    const website = (data.get('website') as string || '').trim() // honeypot

    if(!subject || !message || (!email && !phone)) {
      setError('Please fill required fields')
      return
    }

    try {
      setStatus('sending')
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ name, email, phone, message, subject, website }),
      })

      if (!res.ok) {
        const detail = await res.text().catch(() => '')
        throw new Error(detail || `HTTP ${res.status}`)
      }

      setStatus('sent')
      form.reset()
    } catch (err:any) {
      setStatus('error')
      setError(err?.message || 'Failed to send')
    }
  }

  return (
    <main>
      <Helmet><title>{t('contact.title')} — Pearl of Galia</title></Helmet>

      {/* HERO */}
      <section className="hero">
        <img src="/images/logo-removebg-preview (1).png" alt="Pearl of Galia logo" className="brand-logo-hero" />
        <div className="brand">
          <h1>
            <span>GET IN</span><br/>
            <span>TOUCH</span>
          </h1>
          <p className="tagline">EXCEPTIONAL SERVICE</p>
        </div>
      </section>

      {/* CONTACT FORM SECTION */}
      <section className="room" id="contact-form">
        <div className="contact-container">
          <div className="contact-form-section">
            <h2 className="section-title">{t('contact.form.title')}</h2>
            <p className="contact-intro">{t('contact.form.intro')}</p>

            <form onSubmit={onSubmit} className="modern-contact-form">
              <div className="form-group">
                <label htmlFor="name">{t('contact.form.name')}</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Your full name"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">{t('contact.form.email')} *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">{t('contact.form.phone')}</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">{t('contact.form.subject')} *</label>
                <select id="subject" name="subject" required>
                  <option value="">Select inquiry type</option>
                  <option value="matcha">Matcha Products</option>
                  <option value="caviar">Caviar Selection</option>
                  <option value="tasting">Private Tasting</option>
                  <option value="wholesale">B2B / Wholesale</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">{t('contact.form.message')} *</label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  placeholder="Tell us about your requirements, quantities, delivery location, and any specific needs..."
                  required
                />
              </div>

              {/* Honeypot field */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                style={{position:'absolute',left:'-9999px'}}
                aria-hidden="true"
              />

              <button
                className="btn contact-submit"
                type="submit"
                disabled={status==='sending'}
              >
                {status==='sending' ? t('contact.form.sending') : status==='sent' ? t('contact.form.sent') : t('contact.form.submit')}
              </button>

              {status==='error' && (
                <div className="form-error" role="alert">
                  {error || t('contact.form.error')}
                </div>
              )}

              {status==='sent' && (
                <div className="form-success" role="alert">
                  {t('contact.form.success')}
                </div>
              )}
            </form>
          </div>

          <div className="contact-image">
            <img src="/images/tasting-removebg-preview.png" alt="Pearl of Galia contact" />
          </div>
        </div>
      </section>

      {/* CONTACT INFO */}
      <section className="room">
        <div className="grid">
          <div>
            <h2 className="section-title">{t('contact.details.title')}</h2>

            <div className="contact-details">
              <div className="contact-item">
                <p className="label">{t('contact.details.email')}</p>
                <p><a href="mailto:hello@pearlofgalia.com" className="contact-link">hello@pearlofgalia.com</a></p>
              </div>

              <div className="contact-item">
                <p className="label">{t('contact.details.headquarters')}</p>
                <p dangerouslySetInnerHTML={{__html: t('contact.details.headquarters.address')}}></p>
              </div>

              <div className="contact-item">
                <p className="label">{t('contact.details.delivery')}</p>
                <p dangerouslySetInnerHTML={{__html: t('contact.details.delivery.desc')}}></p>
              </div>

              <div className="contact-item">
                <p className="label">{t('contact.details.response')}</p>
                <p dangerouslySetInnerHTML={{__html: t('contact.details.response.desc')}}></p>
              </div>
            </div>
          </div>

          <div>
            <img src="/images/distribution_1-removebg-preview.png" alt="Pearl of Galia service" />
          </div>
        </div>
      </section>
    </main>
  )
}
